import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Anchor, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/lib/AppContext";
import { t } from "@/lib/i18n";

export default function Login() {
  const { login, lang, setLang } = useApp();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success(t(lang, "welcome"));
      nav("/", { replace: true });
    } catch (err) {
      toast.error(t(lang, "invalid_creds"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-70">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#d4a843]/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#1e3a61]/60 blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto min-h-screen grid lg:grid-cols-2 items-center gap-10 px-6 py-14">
        {/* Left / brand panel */}
        <div className="hidden lg:block anim-fade-up">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#d4a843]/30 bg-[#132744]/60 px-3 py-1 text-[11px] tracking-[0.2em] uppercase text-[#f3c669]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#d4a843]" /> EIGOO Fleet · Scout Access
          </div>
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold mt-6 leading-tight">
            Log referrals across the <span className="gold-gradient-text">entire fleet.</span>
          </h1>
          <p className="mt-5 text-slate-300/80 max-w-md leading-relaxed">
            {t(lang, "login_sub")}
          </p>

          <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
            {["TechOps", "Real Estate", "Logistics", "CyberKids", "Armada SSO", "Command"].map(
              (h) => (
                <div
                  key={h}
                  className="text-[11px] font-mono uppercase tracking-wider text-slate-400 border border-[#1e3a61] rounded-lg px-2.5 py-2 text-center bg-[#132744]/40"
                >
                  {h}
                </div>
              )
            )}
          </div>
        </div>

        {/* Right / auth form */}
        <div className="anim-fade-up anim-delay-1">
          <div className="flex items-center justify-between mb-6 lg:hidden">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl border border-[#d4a843]/50 bg-[#132744] grid place-items-center">
                <Anchor className="w-5 h-5 text-[#f3c669]" />
              </div>
              <div>
                <div className="font-display font-bold">{t(lang, "brand")}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a843]/80">
                  {t(lang, "brand_sub")}
                </div>
              </div>
            </div>
            <div className="inline-flex rounded-xl border border-[#1e3a61] p-0.5 bg-[#0f2038]">
              {["en", "vi"].map((code) => (
                <button
                  key={code}
                  onClick={() => setLang(code)}
                  data-testid={`language-toggle-${code}`}
                  className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-colors ${
                    lang === code
                      ? "bg-gradient-to-r from-[#d4a843] to-[#f3c669] text-[#0a1628]"
                      : "text-slate-300"
                  }`}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="relative eigoo-card p-7 sm:p-9 overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#d4a843]/10 blur-3xl pointer-events-none" />
            <h2 className="font-display text-2xl font-bold">{t(lang, "login_title")}</h2>
            <p className="text-sm text-slate-400 mt-1.5">{t(lang, "demo_hint")}</p>

            <form className="mt-7 space-y-5" onSubmit={submit} data-testid="login-form">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {t(lang, "email")}
                </label>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  data-testid="login-email-input"
                  className="eigoo-input mt-2"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="linro@eigooinc.net"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {t(lang, "password")}
                </label>
                <div className="relative mt-2">
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    data-testid="login-password-input"
                    className="eigoo-input pr-11"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#f3c669]"
                    aria-label="toggle password visibility"
                    data-testid="toggle-password-visibility"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={busy}
                className="gold-btn w-full mt-2"
                data-testid="login-submit-button"
              >
                {busy ? t(lang, "signing_in") : t(lang, "sign_in")}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-slate-500 mt-6">© {new Date().getFullYear()} {t(lang, "footer")}</p>
        </div>
      </div>
    </div>
  );
}
